import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import { toastMessage } from '../../utils/toastMessage';
import { fetchTargetedUsersApi, sendTargetedNotificationApi, scheduleTargetedNotificationApi } from '../../services/notificationManagement';
import Pagination from 'react-js-pagination';

const AutomaticNotification = () => {
  const [filters, setFilters] = useState({
    category: '',
    consultationCount: '',
    timeSinceLast: '',
    customTimeFrom: '',
    customTimeTo: '',
    gender: '',
    waitlistConcern: '',
    waitlistLanguage: '',
    waitlistMaritalStatus: '',
    rechargedNoConsultation: false,
  });

  const [notificationData, setNotificationData] = useState({
    title: '',
    message: '',
    titleHi: '',
    messageHi: '',
    titleKn: '',
    messageKn: '',
    scheduledAt: ''
  });

  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNotificationDataChange = (e) => {
    const { name, value } = e.target;
    setNotificationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fetchUsers = async (page = 1, currentLimit = limit) => {
    setLoading(true);
    try {
      const payload = {
        ...filters,
        page,
        limit: currentLimit
      };
      
      const res = await fetchTargetedUsersApi(payload);
      if (res?.data?.code === 200) {
        setUsers(res.data.result.list || []);
        setTotalUsers(res.data.result.total || 0);
      } else {
        toastMessage(res?.data?.message || 'Failed to fetch users', 'error');
      }
    } catch (error) {
      console.error(error);
      toastMessage('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(activePage, limit);
  }, [activePage, limit]);

  const handleApplyFilters = () => {
    setActivePage(1);
    fetchUsers(1, limit);
  };

  const handleSelectAll = async (e) => {
    if (e.target.checked) {
      setLoading(true);
      try {
        const payload = {
          ...filters,
          fetchAllIds: true
        };
        const res = await fetchTargetedUsersApi(payload);
        if (res?.data?.code === 200) {
          setSelectedUserIds(res.data.result.list || []);
          toastMessage(`Selected all ${res.data.result.total} users`, 'success');
        } else {
          toastMessage('Failed to select all users', 'error');
        }
      } catch (error) {
        console.error(error);
        toastMessage('Error selecting all users', 'error');
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (id) => {
    if (selectedUserIds.includes(id)) {
      setSelectedUserIds(prev => prev.filter(userId => userId !== id));
    } else {
      setSelectedUserIds(prev => [...prev, id]);
    }
  };

  const handleSendNotification = async (isSchedule = false) => {
    if (!notificationData.title || !notificationData.message) {
      toastMessage('Title and message are required', 'error');
      return;
    }
    
    if (selectedUserIds.length === 0) {
      toastMessage('Please select at least one user', 'error');
      return;
    }

    if (isSchedule && !notificationData.scheduledAt) {
      toastMessage('Scheduled time is required', 'error');
      return;
    }

    try {
      const payload = {
        title: notificationData.title,
        message: notificationData.message,
        titleHi: notificationData.titleHi,
        messageHi: notificationData.messageHi,
        titleKn: notificationData.titleKn,
        messageKn: notificationData.messageKn,
        userIds: selectedUserIds
      };

      let res;
      if (isSchedule) {
        const scheduledAt = notificationData.scheduledAt;
        payload.scheduledAt =
          scheduledAt && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(scheduledAt)
            ? `${scheduledAt}:00+05:30`
            : scheduledAt;
        res = await scheduleTargetedNotificationApi(payload);
      } else {
        res = await sendTargetedNotificationApi(payload);
      }

      if (res?.data?.code === 200) {
        toastMessage(`Notification ${isSchedule ? 'scheduled' : 'sent'} successfully`, 'success');
        setNotificationData({ title: '', message: '', titleHi: '', messageHi: '', titleKn: '', messageKn: '', scheduledAt: '' });
        setSelectedUserIds([]);
      } else {
        toastMessage(res?.data?.message || 'Failed to send notification', 'error');
      }
    } catch (error) {
      console.error(error);
      toastMessage('Error sending notification', 'error');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Automatic Targeted Notifications
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Filter Criteria
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
             <FormControl fullWidth size="small">
              <InputLabel>Consultation Count</InputLabel>
              <Select
                name="consultationCount"
                value={filters.consultationCount}
                onChange={handleFilterChange}
                label="Consultation Count"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value=">3">More than 3</MenuItem>
                <MenuItem value="query">Query Only (No Consultation)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
             <FormControl fullWidth size="small">
              <InputLabel>Time Since Last Consultation</InputLabel>
              <Select
                name="timeSinceLast"
                value={filters.timeSinceLast}
                onChange={handleFilterChange}
                label="Time Since Last Consultation"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                <MenuItem value="24h">24 Hours</MenuItem>
                <MenuItem value="48h">48 Hours</MenuItem>
                <MenuItem value="72h">72 Hours</MenuItem>
                <MenuItem value="144h">144 Hours</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {filters.timeSinceLast === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="date"
                  label="Custom Time From"
                  InputLabelProps={{ shrink: true }}
                  name="customTimeFrom"
                  value={filters.customTimeFrom}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="date"
                  label="Custom Time To"
                  InputLabelProps={{ shrink: true }}
                  name="customTimeTo"
                  value={filters.customTimeTo}
                  onChange={handleFilterChange}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={3}>
             <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={filters.gender}
                onChange={handleFilterChange}
                label="Gender"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
             <FormControl fullWidth size="small">
              <InputLabel>Waitlist Concern</InputLabel>
              <Select
                name="waitlistConcern"
                value={filters.waitlistConcern}
                onChange={handleFilterChange}
                label="Waitlist Concern"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="depression">Depression</MenuItem>
                <MenuItem value="career">Career</MenuItem>
                <MenuItem value="marriage">Marriage</MenuItem>
                <MenuItem value="relationship">Relationship</MenuItem>
                <MenuItem value="finance">Finance</MenuItem>
                <MenuItem value="exam">Exam</MenuItem>
                <MenuItem value="health">Health</MenuItem>
                <MenuItem value="family">Family</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
             <FormControl fullWidth size="small">
              <InputLabel>Waitlist Language</InputLabel>
              <Select
                name="waitlistLanguage"
                value={filters.waitlistLanguage}
                onChange={handleFilterChange}
                label="Waitlist Language"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="English">English</MenuItem>
                <MenuItem value="Hindi">Hindi</MenuItem>
                <MenuItem value="Marathi">Marathi</MenuItem>
                <MenuItem value="Gujarati">Gujarati</MenuItem>
                <MenuItem value="Bengali">Bengali</MenuItem>
                <MenuItem value="Tamil">Tamil</MenuItem>
                <MenuItem value="Telugu">Telugu</MenuItem>
                <MenuItem value="Kannada">Kannada</MenuItem>
                <MenuItem value="Malayalam">Malayalam</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
             <FormControl fullWidth size="small">
              <InputLabel>Waitlist Marital Status</InputLabel>
              <Select
                name="waitlistMaritalStatus"
                value={filters.waitlistMaritalStatus}
                onChange={handleFilterChange}
                label="Waitlist Marital Status"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="Single">Single</MenuItem>
                <MenuItem value="Married">Married</MenuItem>
                <MenuItem value="Divorced">Divorced</MenuItem>
                <MenuItem value="Widowed">Widowed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox checked={filters.rechargedNoConsultation} onChange={handleFilterChange} name="rechargedNoConsultation" />}
              label="Recharged But No Consultation"
            />
          </Grid>

          <Grid item xs={12}>
             <Button variant="contained" onClick={handleApplyFilters} sx={{ bgcolor: '#ff8989', '&:hover': { bgcolor: '#ff6b6b' } }}>
               Apply Filters
             </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Targeted Users ({totalUsers})
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={totalUsers > 0 && selectedUserIds.length === totalUsers}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>User ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Consultations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedUserIds.includes(user._id)}
                            onChange={() => handleSelectUser(user._id)}
                          />
                        </TableCell>
                        <TableCell>{user.userId || user._id}</TableCell>
                        <TableCell>{user.name || '---'}</TableCell>
                        <TableCell>{user.email || '---'}</TableCell>
                        <TableCell>{user.phone || '---'}</TableCell>
                        <TableCell>{user.completedSessionCount || 0}</TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">No users found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {totalUsers > 0 && (
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                   <Typography variant="body2">Rows per page:</Typography>
                   <Select
                     size="small"
                     value={limit}
                     onChange={(e) => {
                       setLimit(e.target.value);
                       setActivePage(1);
                     }}
                   >
                     <MenuItem value={20}>20</MenuItem>
                     <MenuItem value={40}>40</MenuItem>
                     <MenuItem value={60}>60</MenuItem>
                     <MenuItem value={80}>80</MenuItem>
                     <MenuItem value={100}>100</MenuItem>
                   </Select>
                 </Box>
                 <div className="Pagination">
                   <Pagination
                      activePage={activePage}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalUsers}
                      pageRangeDisplayed={5}
                      onChange={(page) => setActivePage(page)}
                      prevPageText="‹"
                      nextPageText="›"
                      firstPageText="«"
                      lastPageText="»"
                   />
                 </div>
               </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Notification Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                size="small"
                fullWidth
                label="English Title"
                name="title"
                value={notificationData.title}
                onChange={handleNotificationDataChange}
              />
              <TextField
                size="small"
                fullWidth
                label="English Message"
                name="message"
                multiline
                rows={3}
                value={notificationData.message}
                onChange={handleNotificationDataChange}
              />
              <TextField
                size="small"
                fullWidth
                label="Hindi Title"
                name="titleHi"
                value={notificationData.titleHi}
                onChange={handleNotificationDataChange}
              />
              <TextField
                size="small"
                fullWidth
                label="Hindi Message"
                name="messageHi"
                multiline
                rows={3}
                value={notificationData.messageHi}
                onChange={handleNotificationDataChange}
              />
              <TextField
                size="small"
                fullWidth
                label="Kannada Title"
                name="titleKn"
                value={notificationData.titleKn}
                onChange={handleNotificationDataChange}
              />
              <TextField
                size="small"
                fullWidth
                label="Kannada Message"
                name="messageKn"
                multiline
                rows={3}
                value={notificationData.messageKn}
                onChange={handleNotificationDataChange}
              />
              <TextField
                size="small"
                fullWidth
                type="datetime-local"
                label="Schedule Time"
                InputLabelProps={{ shrink: true }}
                name="scheduledAt"
                value={notificationData.scheduledAt}
                onChange={handleNotificationDataChange}
              />
              
              <Typography variant="caption" color="textSecondary">
                Selected Users: {selectedUserIds.length}
              </Typography>

              <Button 
                variant="contained" 
                onClick={() => handleSendNotification(false)}
                disabled={selectedUserIds.length === 0 || !notificationData.title || !notificationData.message}
                sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
              >
                Send Now
              </Button>

              <Button 
                variant="outlined" 
                onClick={() => handleSendNotification(true)}
                disabled={selectedUserIds.length === 0 || !notificationData.title || !notificationData.message || !notificationData.scheduledAt}
                sx={{ borderColor: '#ff8989', color: '#ff8989', '&:hover': { borderColor: '#ff6b6b' } }}
              >
                Schedule Notification
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AutomaticNotification;
